import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Box, HStack, Icon, SimpleGrid, useToast, Badge, Input, Select,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure, Spinner, Divider,
} from '@chakra-ui/react';
import { FiUpload, FiTrash2, FiPlus } from 'react-icons/fi';
import {
  limpiezaPanteonesApi, UPLOADS_BASE, LimpiezaPanteon, SmArchivo, LimpiezaPanteonPersonal,
} from '../../../api/serviciosApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  registroId: number;
  titulo: string;
  onGuardado: () => void;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={2} mt={2}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">{children}</Text>
  </Box>
);

const ModalLimpiezaPanteonDetalle: React.FC<Props> = ({ isOpen, onClose, registroId, titulo, onGuardado }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [registro, setRegistro] = useState<LimpiezaPanteon | null>(null);
  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [momento, setMomento] = useState<'ANTES' | 'DESPUES'>('ANTES');

  const [pNombre, setPNombre] = useState('');
  const [pRol, setPRol] = useState('');
  const [pObs, setPObs] = useState('');
  const [agregandoPersonal, setAgregandoPersonal] = useState(false);

  const [archivoAEliminar, setArchivoAEliminar] = useState<SmArchivo | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const fetchDetalle = async () => {
    setCargando(true);
    try { setRegistro(await limpiezaPanteonesApi.get(registroId)); } catch { setRegistro(null); } finally { setCargando(false); }
  };

  useEffect(() => { if (isOpen) fetchDetalle(); /* eslint-disable-next-line */ }, [isOpen, registroId]);

  const handleAgregarPersonal = async () => {
    if (!pNombre) { toast({ title: 'El nombre es requerido', status: 'warning', duration: 2500 }); return; }
    setAgregandoPersonal(true);
    try {
      await limpiezaPanteonesApi.addPersonal(registroId, { nombre: pNombre, rol: pRol, observaciones: pObs });
      setPNombre(''); setPRol(''); setPObs('');
      toast({ title: 'Personal agregado', status: 'success', duration: 2000 });
      await fetchDetalle(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally { setAgregandoPersonal(false); }
  };

  const handleRemovePersonal = async (p: LimpiezaPanteonPersonal) => {
    try {
      await limpiezaPanteonesApi.removePersonal(registroId, p.id);
      toast({ title: 'Personal eliminado', status: 'success', duration: 2000 });
      await fetchDetalle(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    }
  };

  const handleUpload = async (file: File) => {
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('momento', momento);
      fd.append('tipoArchivo', 'foto');
      await limpiezaPanteonesApi.addArchivo(registroId, fd);
      toast({ title: `Foto ${momento} subida`, status: 'success', duration: 2000 });
      await fetchDetalle(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error al subir', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally { setSubiendo(false); }
  };

  const handleRemoveArchivoClick = (a: SmArchivo) => { setArchivoAEliminar(a); onOpenEliminar(); };

  const handleConfirmarEliminar = async () => {
    if (!archivoAEliminar) return;
    setEliminando(true);
    try {
      await limpiezaPanteonesApi.removeArchivo(registroId, archivoAEliminar.id);
      toast({ title: 'Archivo eliminado', status: 'success', duration: 2000 });
      onCloseEliminar();
      await fetchDetalle(); onGuardado();
    } catch (error: any) {
      toast({ title: 'Error al eliminar', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally { setEliminando(false); }
  };

  const personal = registro?.personal || [];
  const archivos = registro?.archivos || [];
  const antes = archivos.filter((a) => a.momento === 'ANTES');
  const despues = archivos.filter((a) => a.momento === 'DESPUES');

  const renderThumbs = (arr: SmArchivo[]) => (
    <HStack spacing={2} flexWrap="wrap">
      {arr.length === 0 && <Text fontSize="xs" color="gray.400">Sin fotografías</Text>}
      {arr.map((a) => (
        <Box key={a.id} position="relative" w="80px" h="80px">
          <Box as="a" href={`${UPLOADS_BASE}${a.rutaArchivo}`} target="_blank" display="block"
            w="80px" h="80px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
            <img src={`${UPLOADS_BASE}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
          <Button size="xs" position="absolute" top="2px" right="2px" colorScheme="red"
            borderRadius="full" p={0} minW="18px" h="18px" onClick={() => handleRemoveArchivoClick(a)}>
            <Icon as={FiTrash2} boxSize={2.5} />
          </Button>
        </Box>
      ))}
    </HStack>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Detalle de limpieza — {titulo}</ModalHeader>
          <ModalBody py={5}>
            {cargando ? (
              <Box textAlign="center" py={8}><Spinner color="#5b182b" /></Box>
            ) : (
              <VStack spacing={4} align="stretch">
                <SectionTitle>Personal asignado</SectionTitle>
                <VStack align="stretch" spacing={1}>
                  {personal.length === 0 && <Text fontSize="sm" color="gray.400">Sin personal asignado</Text>}
                  {personal.map((p) => (
                    <HStack key={p.id} justify="space-between" borderWidth="1px" borderRadius="md" p={2}>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">{p.nombre}</Text>
                        <Text fontSize="xs" color="gray.500">{p.rol || '—'} {p.observaciones ? `· ${p.observaciones}` : ''}</Text>
                      </Box>
                      <Button size="xs" colorScheme="red" variant="ghost" onClick={() => handleRemovePersonal(p)}><Icon as={FiTrash2} /></Button>
                    </HStack>
                  ))}
                </VStack>
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={2} alignItems="end">
                  <Input size="sm" placeholder="Nombre *" value={pNombre} onChange={(e) => setPNombre(e.target.value)} />
                  <Input size="sm" placeholder="Rol" value={pRol} onChange={(e) => setPRol(e.target.value)} />
                  <Input size="sm" placeholder="Observaciones" value={pObs} onChange={(e) => setPObs(e.target.value)} />
                  <Button size="sm" leftIcon={<Icon as={FiPlus} />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }}
                    onClick={handleAgregarPersonal} isLoading={agregandoPersonal}>Agregar</Button>
                </SimpleGrid>

                <Divider />

                <SectionTitle>Evidencias fotográficas</SectionTitle>
                <HStack>
                  <Select size="sm" w="140px" value={momento} onChange={(e) => setMomento(e.target.value as any)}>
                    <option value="ANTES">ANTES</option>
                    <option value="DESPUES">DESPUES</option>
                  </Select>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
                  <Button size="sm" leftIcon={subiendo ? <Spinner size="xs" /> : <Icon as={FiUpload} />} variant="outline" colorScheme="teal"
                    onClick={() => fileRef.current?.click()} isDisabled={subiendo}>Subir foto</Button>
                </HStack>
                <Box>
                  <Badge colorScheme="orange" mb={2}>ANTES</Badge>
                  {renderThumbs(antes)}
                </Box>
                <Box>
                  <Badge colorScheme="green" mb={2}>DESPUES</Badge>
                  {renderThumbs(despues)}
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Eliminar archivo</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar <strong>{archivoAEliminar?.nombreArchivo}</strong>? Esta acción no se puede deshacer.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={eliminando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleConfirmarEliminar} ml={3} isLoading={eliminando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ModalLimpiezaPanteonDetalle;
